import { yupResolver } from '@hookform/resolvers/yup';
import ProfileResult from '@molecules/ProfileResult';
import PersonSearch from '@organisms/PersonSearch';
import { useGetProfile } from '@sunrise-ui/api-client';
import { withAuthGate } from '@sunrise-ui/composites';
import {
  Button,
  ButtonVariants,
  Dropdown,
  InputError,
  Loader,
  TextInput,
  useAuthStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { object, string, type InferType } from 'yup';

const hourlyTimeOptions = [
  { label: '12:00AM', value: '00:00' },
  { label: '12:30AM', value: '00:30' },
  { label: '1:00AM', value: '01:00' },
  { label: '1:30AM', value: '01:30' },
  { label: '2:00AM', value: '02:00' },
  { label: '2:30AM', value: '02:30' },
  { label: '3:00AM', value: '03:00' },
  { label: '3:30AM', value: '03:30' },
  { label: '4:00AM', value: '04:00' },
  { label: '4:30AM', value: '04:30' },
  { label: '5:00AM', value: '05:00' },
  { label: '5:30AM', value: '05:30' },
  { label: '6:00AM', value: '06:00' },
  { label: '6:30AM', value: '06:30' },
  { label: '7:00AM', value: '07:00' },
  { label: '7:30AM', value: '07:30' },
  { label: '8:00AM', value: '08:00' },
  { label: '8:30AM', value: '08:30' },
  { label: '9:00AM', value: '09:00' },
  { label: '9:30AM', value: '09:30' },
  { label: '10:00AM', value: '10:00' },
  { label: '10:30AM', value: '10:30' },
  { label: '11:00AM', value: '11:00' },
  { label: '11:30AM', value: '11:30' },
  { label: '12:00PM', value: '12:00' },
  { label: '12:30PM', value: '12:30' },
  { label: '1:00PM', value: '13:00' },
  { label: '1:30PM', value: '13:30' },
  { label: '2:00PM', value: '14:00' },
  { label: '2:30PM', value: '14:30' },
  { label: '3:00PM', value: '15:00' },
  { label: '3:30PM', value: '15:30' },
  { label: '4:00PM', value: '16:00' },
  { label: '4:30PM', value: '16:30' },
  { label: '5:00PM', value: '17:00' },
  { label: '5:30PM', value: '17:30' },
  { label: '6:00PM', value: '18:00' },
  { label: '6:30PM', value: '18:30' },
  { label: '7:00PM', value: '19:00' },
  { label: '7:30PM', value: '19:30' },
  { label: '8:00PM', value: '20:00' },
  { label: '8:30PM', value: '20:30' },
  { label: '9:00PM', value: '21:00' },
  { label: '9:30PM', value: '21:30' },
  { label: '10:00PM', value: '22:00' },
  { label: '10:30PM', value: '22:30' },
  { label: '11:00PM', value: '23:00' },
  { label: '11:30PM', value: '23:30' },
];

const schema = object().shape({
  // make sure date is of the format MM/DD/YYYY
  date: string()
    .required('Required')
    .test('is-valid-date', 'Must be MM/DD/YYYY', (value) => {
      const dateTime = DateTime.fromFormat(value, 'MM/dd/yyyy');
      return dateTime.isValid;
    })
    .test('is-not-past', 'Must be in the future', (value) => {
      const dateTime = DateTime.fromFormat(value, 'MM/dd/yyyy');
      return dateTime >= DateTime.now();
    }),
  startTime: string().required('Required'),
  // make sure endTime is after startTime
  endTime: string()
    .required('Required')
    .test('is-after-start-time', 'Must be after start time', function (value) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { startTime } = this.parent;
      if (!startTime || !value) {
        return true;
      }

      return parseInt(startTime as string, 10) < parseInt(value, 10);
    }),
});

type EventFormValues = InferType<typeof schema>;

function ScheduleEventView() {
  const styles = useDynamicStyles(createStyles);
  const { currentUserId } = useAuthStore();
  const [selectedUser, setSelectedUser] = useState<string>();
  const { isLoading: isLoadingCurrentProfile } = useGetProfile(currentUserId);
  const { isLoading: isLoadingSelectedProfile } = useGetProfile(selectedUser);
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<EventFormValues>({
    defaultValues: {
      date: '',
      startTime: '',
      endTime: '',
    },
    resolver: yupResolver(schema),
  });

  if (isLoadingCurrentProfile || (selectedUser && isLoadingSelectedProfile)) {
    return <Loader />;
  }

  const saveEvent = async () => {
    // TODO: Functionality temporarily disabled - community selection not supported yet
    // const invitedName = `${selectedProfile?.first_name} ${selectedProfile?.last_name}`;
    // await createEvent({
    //   communityId,
    //   createEventInput: {
    //     name: `${invitedName} <> ${currentProfile?.first_name} ${currentProfile?.last_name}`,
    //     start_date: formatIsoUtcFromDateAndTime(date, startTime),
    //     scheduled_end_date: formatIsoUtcFromDateAndTime(date, endTime),
    //     experience_type: ExperienceType.PrivateRoom,
    //     user_id: currentUserId,
    //   },
    // });
    router.navigate('/events');
    // showSnackbar({
    //   text: `Meeting scheduled. ${invitedName} has been invited.`,
    //   type: 'success',
    // });
  };

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {selectedUser ? (
          <>
            <ProfileResult userId={selectedUser} />
            <View style={styles.scheduleForm}>
              <View style={styles.formField}>
                <Controller
                  control={control}
                  render={({ field }) => (
                    <TextInput label="MM/DD/YYYY" {...field} />
                  )}
                  name="date"
                />
                <InputError fieldError={errors.date} />
              </View>
              <View style={styles.formField}>
                <Controller
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Dropdown
                      placeholder="Start"
                      options={hourlyTimeOptions}
                      value={value}
                      onChange={onChange}
                      onClear={() => onChange('')}
                    />
                  )}
                  name="startTime"
                />
                <InputError fieldError={errors.startTime} />
              </View>
              <View style={styles.formField}>
                <Controller
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Dropdown
                      placeholder="End"
                      options={hourlyTimeOptions}
                      value={value}
                      onChange={onChange}
                      onClear={() => onChange('')}
                    />
                  )}
                  name="endTime"
                />
                <InputError fieldError={errors.endTime} />
              </View>
            </View>
            <Button
              style={styles.button}
              variant={ButtonVariants.PURPLE}
              onPress={handleSubmit(saveEvent)}
              loading={isSubmitting}
            >
              Save
            </Button>
          </>
        ) : (
          <PersonSearch
            onSelect={(profile) => {
              setSelectedUser(profile.user_id);
            }}
            maxHeight={360}
          />
        )}
      </View>
    </View>
  );
}

const createStyles = ({ isMobile, isTablet }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      padding: isMobile ? 12 : isTablet ? 16 : 20,
      alignItems: 'center',
    },
    content: {
      width: isMobile ? '100%' : isTablet ? '75%' : '50%',
      flexDirection: 'column',
      gap: 20,
    },
    scheduleForm: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: 20,
    },
    formField: {
      flexGrow: 1,
    },
    button: {
      width: 100,
      alignSelf: 'center',
    },
  });

export default withAuthGate(ScheduleEventView);
